import * as T from 'three';
import {retainModel} from './model-assets.mjs';
const cache=new Map(), references=new Map(), materialKeys=new WeakMap();
function material(color,emissive=null,metalness=.25,roughness=.5){const key=[color,emissive,metalness,roughness].join();if(!cache.has(key)){const value=new T.MeshStandardMaterial({color,emissive:emissive||'#000000',emissiveIntensity:emissive?.7:0,metalness,roughness});cache.set(key,value);materialKeys.set(value,key);}return cache.get(key);}
const geo={box:new T.BoxGeometry(1,1,1),sphere:new T.SphereGeometry(.5,24,16),cylinder:new T.CylinderGeometry(.5,.5,1,32),cone:new T.ConeGeometry(.5,1,24),torus:new T.TorusGeometry(.5,.055,12,64)};
const mesh=(g,m,p,s,parent)=>{let x=new T.Mesh(g,m);x.position.set(...p);x.scale.set(...s);x.castShadow=true;x.receiveShadow=true;parent.add(x);return x;};

// References belong to individual entities, not whole scenes: an incremental
// removal must leave shared primitives/materials in all other worlds alive.
function retainRecord(record) {
  const resources=new Set();
  record.group.traverse(object=>{if(object.geometry)resources.add(object.geometry);});
  for(const value of record.materials.values())resources.add(value);
  for(const value of record.pulseMaterials?.values()??[])resources.add(value);
  for(const value of resources)references.set(value,(references.get(value)??0)+1);
  let released=false;
  return ()=>{
    if(released)return;released=true;
    for(const value of resources){
      const count=references.get(value)-1;
      if(count)references.set(value,count);
      else {references.delete(value);const key=materialKeys.get(value);if(cache.get(key)===value)cache.delete(key);value.dispose();}
    }
  };
}
function paintMaterial(entity,role) {
  if(role==='dome')return material(entity.color,null,.45,.4);
  if(role==='sculpture')return material(entity.color,entity.emissive||entity.color,.7,.22);
  if(role==='pipe')return material(entity.color,entity.color,.5,.18);
  if(role==='glow')return material(entity.color,entity.color);
  return material(entity.color,entity.emissive);
}
const signature=value=>JSON.stringify(value);
const simpleKinds=new Set(['box','sphere','cylinder','cone','torus','light']);
function makeRecord(e) {
  const g=new T.Group(),s=e.scale,p=e.position,bindings=new Map();
  g.name=`Entity · ${e.id}`;
  g.position.set(p.x,p.y,p.z);g.rotation.set(e.rotation?.x||0,e.rotation?.y||0,e.rotation?.z||0);
  const colored=(geometry,role,position,scale,parent)=>{
    const object=mesh(geometry,paintMaterial(e,role),position,scale,parent);bindings.set(object,role);return object;
  };
 if(e.kind==='model'){
   g.scale.set(s.x,s.y,s.z);let released=false,handle=null;
   const record={group:g,entity:e,snapshot:structuredClone(e),baseY:p.y,bindings,materials:new Map(),pulseRemaining:0,pulseMaterials:null,model:true,retryAt:0,loading:false,release(){released=true;handle?.release();}};
   record.loadModel=()=>{if(released||record.loading||g.userData.modelReady)return;record.loading=true;delete g.userData.modelError;handle=retainModel(e.assetId);const current=handle;
     record.ready=current.ready.then(model=>{if(!released){g.add(model);g.userData.modelReady=true;}return model;}).catch(error=>{current.release();if(!released){g.userData.modelError=error.message;record.retryAt=Date.now()+5000;}}).finally(()=>{record.loading=false;});
   };
   record.loadModel();return record;
 }
 if(e.kind==='island'){colored(new T.CylinderGeometry(.5,.4,.35,7),'body',[0,-s.y*.25,0],[s.x,s.y,s.z],g);mesh(new T.CylinderGeometry(.496,.49,.018,7),material('#526773'),[0,-.02,0],[s.x,1,s.z],g);const rim=new T.Mesh(new T.TorusGeometry(.49,.003,5,7),material('#86b9cf','#5095c4'));rim.rotation.x=Math.PI/2;rim.scale.set(s.x,s.z,1);rim.position.y=.01;g.add(rim);}
 else if(e.kind==='bridge'){colored(geo.box,'body',[0,-s.y/2,0],[s.x,s.y,s.z],g);for(const x of [-1,1]){mesh(geo.box,material('#d8b2ff','#7141ab'),[x*(s.x/2-.15),.02,0],[.055,.025,s.z],g);for(let z=-s.z/2+1;z<s.z/2;z+=2)mesh(geo.box,material('#222940'),[x*s.x/2,.5,z],[.06,1,.08],g);mesh(geo.box,material('#a4bed4'),[x*s.x/2,1,0],[.09,.08,s.z],g);}}
 else if(e.kind==='pavilion'||e.kind==='architecture'){const h=s.y,w=s.x,d=s.z;colored(new T.CylinderGeometry(.5,.54,.08,8),'body',[0,.04,0],[w,1,d],g);for(let i=0;i<8;i++){const a=i*Math.PI/4;colored(geo.cylinder,'body',[Math.sin(a)*w*.43,h*.45,Math.cos(a)*d*.43],[.35,h*.9,.35],g);}const cap=colored(new T.SphereGeometry(.5,32,16,0,Math.PI*2,0,Math.PI/2),'dome',[0,h*.9,0],[w,h*.35,d],g);for(const y of [h*.85,h*.9]){const r=mesh(new T.TorusGeometry(.5,.012,8,48),material('#d7baec','#4d335e'),[0,y,0],[w,d,1],g);r.rotation.x=Math.PI/2;}for(let i=0;i<12;i++){const a=i/12*Math.PI*2;mesh(geo.box,material('#d7dcf3'),[Math.sin(a)*w*.49,.11,Math.cos(a)*d*.49],[.6,.12,.6],g);}}
 else if(e.kind==='tree'){mesh(geo.cylinder,material('#4d4257'),[0,s.y*.3,0],[s.x*.12,s.y*.6,s.x*.12],g);for(let i=0;i<3;i++)colored(geo.sphere,'body',[Math.sin(i*2.4)*s.x*.15,s.y*(.6+i*.13),Math.cos(i*2.4)*s.z*.15],[s.x*(.9-i*.17),s.y*.44,s.z*(.9-i*.17)],g);}
 else if(e.kind==='sculpture'){const r=colored(geo.torus,'sculpture',[0,s.y*.5,0],[s.x,s.y,s.z],g);r.rotation.y=.45;mesh(geo.cylinder,material('#364055'),[0,.12,0],[s.x*.6,.24,s.z*.6],g);mesh(new T.OctahedronGeometry(.5),material('#f2daff','#956cac'),[0,s.y*.5,0],[s.x*.45,s.y*.5,s.z*.45],g);}
 else if(e.kind==='instrument'){mesh(geo.cylinder,material('#34384e'),[0,-.06,0],[s.x*1.4,.12,s.z*1.4],g);for(let i=0;i<5;i++){const h=s.y*(.55+Math.sin(i/4*Math.PI)*.45);colored(new T.CylinderGeometry(.09,.13,1,8),'pipe',[(i-2)*s.x*.2,h/2,0],[1,h,1],g);}const ring=colored(geo.torus,'glow',[0,.04,0],[s.x*1.25,s.z*1.25,1],g);ring.rotation.x=Math.PI/2;}
 else if(e.kind==='light'){colored(geo.sphere,'glow',[0,0,0],[s.x,s.y,s.z],g);}
 else colored(geo[e.kind]||geo.box,'body',[0,0,0],[s.x,s.y,s.z],g);
  const materials=new Map();g.traverse(object=>{if(object.material)materials.set(object,object.material);});
  const record={group:g,entity:e,snapshot:structuredClone(e),baseY:p.y,bindings,materials,pulseRemaining:0,pulseMaterials:null};
  record.release=retainRecord(record);
  return record;
}
function refreshResources(record,deferDispose) {
  const previous=record.release;record.release=retainRecord(record);deferDispose(previous);
}
function releaseRecord(record,deferDispose) {
  record.group.removeFromParent();deferDispose(record.release);
}
function setPaint(record,entity,deferDispose) {
  for(const [object,role]of record.bindings){
    const base=paintMaterial(entity,role);
    if(record.pulseMaterials){
      const value=base.clone();value.emissiveIntensity=2;record.pulseMaterials.set(object,base);
      object.material=value;record.materials.set(object,value);
    } else {object.material=base;record.materials.set(object,base);}
  }
  refreshResources(record,deferDispose);
}
function endPulse(record,deferDispose) {
  if(!record.pulseMaterials)return;
  for(const [object,base]of record.pulseMaterials){object.material=base;record.materials.set(object,base);}
  record.pulseMaterials=null;refreshResources(record,deferDispose);
}
function placeRecord(record,next,previous,time) {
  const group=record.group;
  // Retain accumulated spin (and any current phase) while moving its base pose.
  for(const axis of ['x','y','z']){
    group.position[axis]+=next.position[axis]-previous.position[axis];
    group.rotation[axis]+=(next.rotation?.[axis]??0)-(previous.rotation?.[axis]??0);
  }
  if(signature(next.capabilities)!==signature(previous.capabilities)||next.position.x!==previous.position.x||next.position.y!==previous.position.y){
    const hover=next.capabilities?.find(cap=>cap.type==='hover');
    group.position.y=next.position.y+(hover?Math.sin(time*hover.speed+next.position.x)*hover.amplitude:0);
  }
  if(next.kind==='model')group.scale.set(next.scale.x,next.scale.y,next.scale.z);
  if(simpleKinds.has(next.kind)&&signature(next.scale)!==signature(previous.scale))group.children[0].scale.set(next.scale.x,next.scale.y,next.scale.z);
  record.baseY=next.position.y;
}
export function createEntities(world,{time=0}={}) {
  const root=new T.Group(),objects=new Map(),interactables=[];
  let elapsed=time,disposed=false;
  const api={root,objects,interactables,
    retryModels(now=Date.now()){for(const record of objects.values())if(record.model&&record.group.userData.modelError&&now>=record.retryAt)record.loadModel();},
    get ready(){return Promise.all([...objects.values()].map(r=>r.ready));},
    get modelErrors(){return [...objects.values()].filter(r=>r.group.userData.modelError).map(r=>r.group.userData.modelError);},
    get modelsReady(){return [...objects.values()].every(r=>!r.model||r.group.userData.modelReady);},
    updateWorld(next,{deferDispose=release=>release()}={}) {
      if(disposed)throw new Error('Entity view is disposed');
      const definitions=next.entities??[],ids=new Set(),prepared=new Map();
      // Build every replacement before touching the live map. This API consumes
      // admitted backend definitions; malformed/duplicate IDs cannot half-apply.
      try {
        for(const entity of definitions){
          if(!entity.id||ids.has(entity.id))throw new Error('Entity IDs must be unique');ids.add(entity.id);
          const old=objects.get(entity.id);
          if(!old||old.snapshot.kind!==entity.kind||old.snapshot.assetId!==entity.assetId||(!simpleKinds.has(entity.kind)&&signature(old.snapshot.scale)!==signature(entity.scale)))prepared.set(entity.id,makeRecord(entity));
        }
      } catch(error){for(const record of prepared.values())releaseRecord(record,release=>release());throw error;}
      const changes={added:[],removed:[],replaced:[],updated:[]};
      for(const [id,record]of objects)if(!ids.has(id)){releaseRecord(record,deferDispose);objects.delete(id);changes.removed.push(id);}
      for(const entity of definitions){
        const old=objects.get(entity.id),replacement=prepared.get(entity.id);
        if(replacement){
          if(old){
            // Geometry changed, but its animation does not restart at revision 0.
            replacement.group.rotation.y+=old.group.rotation.y-(old.snapshot.rotation?.y??0);
            releaseRecord(old,deferDispose);changes.replaced.push(entity.id);
          } else changes.added.push(entity.id);
          const hover=entity.capabilities?.find(cap=>cap.type==='hover');
          if(hover)replacement.group.position.y=entity.position.y+Math.sin(elapsed*hover.speed+entity.position.x)*hover.amplitude;
          objects.set(entity.id,replacement);root.add(replacement.group);
        } else {
          if(signature(old.snapshot)!==signature(entity)){
            placeRecord(old,entity,old.snapshot,elapsed);
            if(!old.model&&(old.snapshot.color!==entity.color||old.snapshot.emissive!==entity.emissive))setPaint(old,entity,deferDispose);
            old.snapshot=structuredClone(entity);changes.updated.push(entity.id);
          }
          old.entity=entity;
        }
      }
      interactables.splice(0,interactables.length,...definitions.filter(entity=>entity.kind==='instrument'));
      return changes;
    },
    update(dt,time){
      if(disposed)return;api.retryModels();elapsed=Number.isFinite(time)?time:elapsed+dt;
      for(const record of objects.values()){
        const {group,entity,baseY}=record;
        for(const cap of entity.capabilities??[]){
          if(cap.type==='hover')group.position.y=baseY+Math.sin(elapsed*cap.speed+entity.position.x)*cap.amplitude;
          if(cap.type==='spin')group.rotation.y+=dt*cap.speed;
        }
        if(record.pulseMaterials){record.pulseRemaining-=dt;if(record.pulseRemaining<=0)endPulse(record,release=>release());}
      }
    },
    pulse(id){
      const record=objects.get(id);if(!record||disposed||record.model)return;
      record.pulseRemaining=.4;
      if(record.pulseMaterials)return;
      record.pulseMaterials=new Map();
      for(const [object,base]of record.materials){
        if(!base.emissive)continue;
        const value=base.clone();value.emissiveIntensity=2;record.pulseMaterials.set(object,base);object.material=value;record.materials.set(object,value);
      }
      // Keep base materials retained for restoration as well as pulse clones.
      refreshResources(record,release=>release());
    },
    dispose({deferDispose=release=>release()}={}){
      if(disposed)return;disposed=true;for(const record of objects.values())releaseRecord(record,deferDispose);objects.clear();interactables.length=0;root.removeFromParent();
    },
  };
  api.updateWorld(world);return api;
}
export function createPortal(portal,color='#95cfff'){const g=new T.Group();g.position.set(portal.position.x,portal.position.y,portal.position.z);const m=material('#eee3e0',null,.35,.4),glow=material(color,color,.3,.25);for(const side of [-1,1]){mesh(geo.box,m,[side*1.8,1.5,0],[.35,3,.5],g);mesh(geo.box,glow,[side*1.59,1.5,0],[.06,3,.54],g);}const arch=mesh(new T.TorusGeometry(1.8,.18,12,32,Math.PI),m,[0,3,0],[1,1,1],g);const rim=mesh(new T.TorusGeometry(1.6,.025,8,32,Math.PI),glow,[0,3,.02],[1,1,1],g);const aura=new T.Mesh(new T.PlaneGeometry(3.2,4.8),new T.MeshBasicMaterial({color,transparent:true,opacity:.07,side:T.DoubleSide,depthWrite:false}));aura.position.y=2.1;g.add(aura);const floor=mesh(new T.CylinderGeometry(2.3,2.3,.09,48),material('#282b41'),[0,.035,0],[1,1,1],g);return g;}
